/**
 * SearchFormComponent - Hero banner search form with validation
 * Handles form interactions, validation, and submission for travel search
 */

import { SearchFormComponent as ISearchFormComponent, SearchData, InputField, DateField, NumberField } from '../types/components';
import { ValidationResult } from '../types/validation';
import { validateNumber, validateDestination } from '../utils/validation.js';
import { ErrorHandler } from '../utils/ErrorHandler';
import { errorLogger } from '../utils/ErrorLogger';

export class SearchFormComponent implements ISearchFormComponent {
  private form: HTMLFormElement;
  private currentSearchType: 'flights' | 'hotels' | 'trains' = 'flights';
  private isSubmitting = false;
  private errorHandler: ErrorHandler;

  // Form fields
  destination: InputField;
  departureDate: DateField;
  returnDate: DateField;
  passengers: NumberField;
  searchType: 'flights' | 'hotels' | 'trains';
  onSubmit: (data: SearchData) => void;

  constructor(formElement: HTMLFormElement, onSubmit: (data: SearchData) => void) {
    this.form = formElement;
    this.onSubmit = onSubmit;
    this.searchType = this.currentSearchType;
    this.errorHandler = ErrorHandler.getInstance();

    // Initialize form fields with default values
    this.destination = {
      id: 'destination',
      name: 'destination',
      type: 'text',
      placeholder: 'Où souhaitez-vous aller ?',
      value: '',
      required: true
    };

    this.departureDate = {
      id: 'departure-date',
      name: 'departureDate',
      placeholder: '',
      value: null,
      required: true,
      minDate: new Date()
    };

    this.returnDate = {
      id: 'return-date',
      name: 'returnDate',
      placeholder: '',
      value: null,
      required: false,
      minDate: new Date()
    };

    this.passengers = {
      id: 'passengers',
      name: 'passengers',
      placeholder: 'Nombre de voyageurs',
      value: 2,
      min: 1,
      max: 8,
      required: true
    };

    // Initialize form fields
    this.initializeFields();
    
    // Bind event listeners
    this.bindEvents();
    
    // Set initial state
    this.updateFormForSearchType();
  }

  private initializeFields(): void {
    // Set minimum dates to today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    this.departureDate.minDate = today;
    this.returnDate.minDate = today;
    
    // Set default departure date to today
    const todayString = today.toISOString().split('T')[0]!;
    const departureDateInput = this.form.querySelector('#departure-date') as HTMLInputElement;
    if (departureDateInput && !departureDateInput.value) {
      departureDateInput.min = todayString;
    }
    
    const returnDateInput = this.form.querySelector('#return-date') as HTMLInputElement;
    if (returnDateInput && !returnDateInput.value) {
      returnDateInput.min = todayString;
    }
  }

  private bindEvents(): void {
    // Tab switching
    const tabs = this.form.querySelectorAll('.search-form__tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        const button = e.target as HTMLButtonElement;
        const searchType = button.dataset.searchType as 'flights' | 'hotels' | 'trains';
        if (searchType) {
          this.switchSearchType(searchType);
        }
      });
    });

    // Form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    // Real-time validation
    const inputs = this.form.querySelectorAll('.search-form__input');
    inputs.forEach(input => {
      input.addEventListener('blur', () => {
        this.validateField(input as HTMLInputElement | HTMLSelectElement);
      });
      
      input.addEventListener('input', () => {
        this.clearFieldError(input as HTMLInputElement | HTMLSelectElement);
      });
    });

    // Date field dependencies
    const departureDateInput = this.form.querySelector('#departure-date') as HTMLInputElement;
    const returnDateInput = this.form.querySelector('#return-date') as HTMLInputElement;
    
    if (departureDateInput && returnDateInput) {
      departureDateInput.addEventListener('change', () => {
        this.updateReturnDateMinimum();
      });
    }
  }

  private switchSearchType(searchType: 'flights' | 'hotels' | 'trains'): void {
    this.currentSearchType = searchType;
    this.searchType = searchType;
    
    // Update active tab
    const tabs = this.form.querySelectorAll('.search-form__tab');
    tabs.forEach(tab => {
      const button = tab as HTMLButtonElement;
      const isActive = button.dataset.searchType === searchType;
      
      button.classList.toggle('search-form__tab--active', isActive);
      button.setAttribute('aria-selected', isActive.toString());
    });

    // Update form for search type
    this.updateFormForSearchType();
  }

  private updateFormForSearchType(): void {
    const returnDateLabel = this.form.querySelector('label[for="return-date"]');
    
    if (returnDateLabel) {
      switch (this.currentSearchType) {
        case 'hotels':
          returnDateLabel.textContent = 'Date de départ';
          returnDateLabel.classList.add('required');
          this.returnDate.required = true;
          break;
        case 'trains':
        case 'flights':
        default:
          returnDateLabel.textContent = 'Date de retour';
          returnDateLabel.classList.remove('required');
          this.returnDate.required = false;
          break;
      }
    }

    // Update placeholder text based on search type
    const destinationInput = this.form.querySelector('#destination');
    if (destinationInput) {
      switch (this.currentSearchType) {
        case 'hotels':
          (destinationInput as HTMLInputElement).placeholder = 'Quelle ville ou région ?';
          break;
        case 'trains':
          (destinationInput as HTMLInputElement).placeholder = 'Quelle gare de destination ?';
          break;
        case 'flights':
        default:
          (destinationInput as HTMLInputElement).placeholder = 'Où souhaitez-vous aller ?';
          break;
      }
    }
  }

  private updateReturnDateMinimum(): void {
    const departureDateInput = this.form.querySelector('#departure-date') as HTMLInputElement;
    const returnDateInput = this.form.querySelector('#return-date') as HTMLInputElement;
    
    if (departureDateInput && returnDateInput && departureDateInput.value) {
      const departureDate = new Date(departureDateInput.value);
      const nextDay = new Date(departureDate);
      nextDay.setDate(nextDay.getDate() + 1);
      
      returnDateInput.min = nextDay.toISOString().split('T')[0]!;
      this.returnDate.minDate = nextDay;
      
      // Clear return date if it's before departure date
      if (returnDateInput.value) {
        const returnDate = new Date(returnDateInput.value);
        if (returnDate <= departureDate) {
          returnDateInput.value = '';
          this.returnDate.value = null;
        }
      }
    }
  }

  private validateField(input: HTMLInputElement | HTMLSelectElement): ValidationResult {
    const fieldName = input.name;
    const value = input.value;
    let result: ValidationResult = { field: fieldName, isValid: true };

    // Clear previous errors
    this.clearFieldError(input);

    switch (fieldName) {
      case 'destination':
        if (!value.trim()) {
          result = { field: fieldName, isValid: false, message: 'La destination est requise' };
        } else {
          result = validateDestination(value);
        }
        break;
        
      case 'departureDate':
        if (!value) {
          result = { field: fieldName, isValid: false, message: 'La date de départ est requise' };
        } else {
          const date = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (date < today) {
            result = { field: fieldName, isValid: false, message: 'La date de départ ne peut pas être dans le passé' };
          }
        }
        break;
        
      case 'returnDate':
        if (this.returnDate.required && !value) {
          result = { field: fieldName, isValid: false, message: 'La date de retour est requise' };
        } else if (value) {
          const returnDate = new Date(value);
          const departureDateInput = this.form.querySelector('#departure-date') as HTMLInputElement;
          
          if (departureDateInput.value) {
            const departureDate = new Date(departureDateInput.value);
            if (returnDate <= departureDate) {
              result = { field: fieldName, isValid: false, message: 'La date de retour doit être après la date de départ' };
            }
          }
        }
        break;
        
      case 'passengers':
        if (!value) {
          result = { field: fieldName, isValid: false, message: 'Le nombre de voyageurs est requis' };
        } else {
          const num = parseInt(value, 10);
          result = validateNumber(num, 1, 8, 'Le nombre de voyageurs doit être entre 1 et 8');
        }
        break;
    }

    // Display error if validation failed
    if (!result.isValid && result.message) {
      this.displayFieldError(input, result.message);
    }

    return result;
  }

  private validateForm(): ValidationResult[] {
    const inputs = this.form.querySelectorAll('.search-form__input') as NodeListOf<HTMLInputElement | HTMLSelectElement>;
    const results: ValidationResult[] = [];

    inputs.forEach(input => {
      const result = this.validateField(input);
      results.push(result);
    });

    return results;
  }

  private displayFieldError(input: HTMLInputElement | HTMLSelectElement, message: string): void {
    const errorElement = this.form.querySelector(`#${input.id}-error`) as HTMLElement;
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = 'block';
    }
    
    input.classList.add('error');
    input.setAttribute('aria-invalid', 'true');
  }

  private clearFieldError(input: HTMLInputElement | HTMLSelectElement): void {
    const errorElement = this.form.querySelector(`#${input.id}-error`) as HTMLElement;
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.style.display = 'none';
    }
    
    input.classList.remove('error');
    input.removeAttribute('aria-invalid');
  }

  private clearAllErrors(): void {
    const inputs = this.form.querySelectorAll('.search-form__input') as NodeListOf<HTMLInputElement | HTMLSelectElement>;
    inputs.forEach(input => {
      this.clearFieldError(input);
    });
  }

  private getFormData(): SearchData {
    const destinationInput = this.form.querySelector('#destination') as HTMLInputElement;
    const departureDateInput = this.form.querySelector('#departure-date') as HTMLInputElement;
    const returnDateInput = this.form.querySelector('#return-date') as HTMLInputElement;
    const passengersInput = this.form.querySelector('#passengers') as HTMLSelectElement;

    const searchData: SearchData = {
      destination: destinationInput.value.trim(),
      departureDate: new Date(departureDateInput.value),
      passengers: parseInt(passengersInput.value, 10),
      searchType: this.currentSearchType
    };

    if (returnDateInput.value) {
      searchData.returnDate = new Date(returnDateInput.value);
    }

    return searchData;
  }

  private setSubmittingState(isSubmitting: boolean): void {
    this.isSubmitting = isSubmitting;
    const submitButton = this.form.querySelector('.search-form__submit') as HTMLButtonElement;
    
    if (submitButton) {
      submitButton.disabled = isSubmitting;
      submitButton.classList.toggle('loading', isSubmitting);
      
      if (isSubmitting) {
        submitButton.setAttribute('aria-busy', 'true');
      } else {
        submitButton.removeAttribute('aria-busy');
      }
    }
  }

  private async handleSubmit(): Promise<void> {
    if (this.isSubmitting) return;

    // Clear previous errors
    this.clearAllErrors();

    // Validate form
    const validationResults = this.validateForm();
    const hasErrors = validationResults.some(result => !result.isValid);

    if (hasErrors) {
      // Focus first invalid field
      const firstErrorField = this.form.querySelector('.search-form__input.error') as HTMLElement;
      if (firstErrorField) {
        firstErrorField.focus();
      }
      return;
    }

    // Set loading state
    this.setSubmittingState(true);

    try {
      // Get form data
      const formData = this.getFormData();
      
      // Call submit handler
      await this.onSubmit(formData);
      
    } catch (error) {
      console.error('Search form submission error:', error);
      
      // Log error for debugging
      errorLogger.logError({
        type: 'client',
        code: 'SEARCH_FORM_ERROR',
        message: 'Search form submission failed',
        details: { error: error instanceof Error ? error.message : error },
        timestamp: new Date()
      });
      
      // Show user-friendly error message
      const appError = ErrorHandler.createValidationError(
        'search-form',
        'Une erreur est survenue lors de la recherche. Veuillez réessayer.'
      );
      this.errorHandler.handleError(appError);
    } finally {
      // Remove loading state
      this.setSubmittingState(false);
    }
  }

  // Protected methods for inheritance
  protected getForm(): HTMLFormElement {
    return this.form;
  }

  // Public methods
  public reset(): void {
    this.form.reset();
    this.clearAllErrors();
    this.switchSearchType('flights');
  }

  public setDestination(destination: string): void {
    const destinationInput = this.form.querySelector('#destination') as HTMLInputElement;
    if (destinationInput) {
      destinationInput.value = destination;
      this.destination.value = destination;
    }
  }

  public getSearchType(): 'flights' | 'hotels' | 'trains' {
    return this.currentSearchType;
  }
}